// @flow

import { createAction, handleActions } from 'redux-actions'
import { call, put, select, takeEvery } from 'redux-saga/effects'
import RestClient from 'frontend_core/dist/rest/client'
import { showErrorMessage } from 'helpers/error'
import { SET_UI_STATE } from '../ui'

/**
 ************************************************************
 * 初期ステート
 ************************************************************
 **/

const initialState = {
  user: {
    contact: {},
  },
  payments: [],
  isFetching: false,
  isSuccess: false,
  isFailed: false,

  isUpdating: false,
  isSuccessUpdate: false,
  isFailedUpdate: false,

  isSuccessSendPhoneNumber: false,
  isFailedSendPhoneNumber: false,
  sendPhoneNumberError: '',
  isSuccessVerifyCode: false,
  isFailedVerifyCode: false,
  verifyCodeError: '',
}

/**
 ************************************************************
 * ActionTypes
 ************************************************************
 **/

const BASE_ACTION = '@@user'
export const actionTypes = {
  FETCH_USER: `${BASE_ACTION}/FETCH_USER`,
  SET_USER: `${BASE_ACTION}/SET_REMOTE_USER`,
  FETCH_FAILED_USER: `${BASE_ACTION}/FETCH_FAILED_USER`,

  UPDATE_USER: `${BASE_ACTION}/UPDATE_USER`,
  SUCCESS_UPDATE_USER: `${BASE_ACTION}/SUCCESS_UPDATE_USER`,
  FAILED_UPDATE_USER: `${BASE_ACTION}/FAILED_UPDATE_USER`,

  VERIFY_PHONE_SMS: `${BASE_ACTION}/VERIFY_PHONE_SMS`,
  VERIFY_PHONE_CALL: `${BASE_ACTION}/VERIFY_PHONE_CALL`,
  SUCCESS_VERIFY_PHONE: `${BASE_ACTION}/SUCCESS_VERIFY_PHONE`,
  FAILED_VERIFY_PHONE: `${BASE_ACTION}/FAILED_VERIFY_PHONE`,

  CONFIRM_VERIFY_CODE: `${BASE_ACTION}/CONFIRM_VERIFY_CODE`,
  SUCCESS_VERIFY_CODE: `${BASE_ACTION}/SUCCESS_VERIFY_CODE`,
  FAILED_VERIFY_CODE: `${BASE_ACTION}/FAILED_VERIFY_CODE`,

  FETCH_PAYMENTS: `${BASE_ACTION}/FETCH_PAYMENTS`,
  SET_PAYMENTS: `${BASE_ACTION}/SET_PAYMENTS`,

  RECALC_GUEST_AMOUNT: `${BASE_ACTION}/RECALC_GUEST_AMOUNT`,
  PAY_RESERVATION: `${BASE_ACTION}/PAY_RESERVATION`,
  POST_PAYMENT: `${BASE_ACTION}/POST_PAYMENT`,
  ADD_PAYMENT: `${BASE_ACTION}/ADD_PAYMENT`,
  REMOVE_POINT: `${BASE_ACTION}/REMOVE_POINT`,
  REMOVE_COUPON: `${BASE_ACTION}/REMOVE_COUPON`,
  CREATE_PAID_CONTACT: `${BASE_ACTION}/CREATE_PAID_CONTACT`,

  CHANGE_PAYMENT: `${BASE_ACTION}/CHANGE_PAYMENT`,
}

/**
 ************************************************************
 * Actions
 ************************************************************
 **/

export const actions = {
  fetchUser: createAction(actionTypes.FETCH_USER),
  updateUser: createAction(actionTypes.UPDATE_USER),
  verifyPhoneSms: createAction(actionTypes.VERIFY_PHONE_SMS),
  verifyPhoneCall: createAction(actionTypes.VERIFY_PHONE_CALL),
  confirmVerifyCode: createAction(actionTypes.CONFIRM_VERIFY_CODE),
  fetchPayments: createAction(actionTypes.FETCH_PAYMENTS),
  recalcGuestAmount: createAction(actionTypes.RECALC_GUEST_AMOUNT),
  payReservation: createAction(actionTypes.PAY_RESERVATION),
  postPayment: createAction(actionTypes.POST_PAYMENT),
  addPayment: createAction(actionTypes.ADD_PAYMENT),
  removePoint: createAction(actionTypes.REMOVE_POINT),
  removeCoupon: createAction(actionTypes.REMOVE_COUPON),
  createPaidContact: createAction(actionTypes.CREATE_PAID_CONTACT),
  changePayment: createAction(actionTypes.CHANGE_PAYMENT),
}

/**
 ************************************************************
 * reducer
 ************************************************************
 **/

export const reducer = handleActions(
  {
    [actionTypes.FETCH_USER]: state => ({
      ...state,
      isFetching: true,
      isSuccess: false,
      isFailed: false,
    }),
    [actionTypes.SET_USER]: (state, action) => ({
      ...state,
      user: action.payload,
      isFetching: false,
      isSuccess: true,
      isFailed: false,
    }),
    [actionTypes.FETCH_FAILED_USER]: state => ({
      ...state,
      isFetching: false,
      isSuccess: false,
      isFailed: true,
    }),

    [actionTypes.UPDATE_USER]: state => ({
      ...state,
      isUpdating: true,
      isSuccessUpdate: false,
      isFailedUpdate: false,
    }),
    [actionTypes.SUCCESS_UPDATE_USER]: state => ({
      ...state,
      isUpdating: false,
      isSuccessUpdate: true,
      isFailedUpdate: false,
    }),
    [actionTypes.FETCH_FAILED_USER]: state => ({
      ...state,
      isUpdating: false,
      isSuccessUpdate: false,
      isFailedUpdate: true,
    }),

    [actionTypes.VERIFY_PHONE_SMS]: state => ({
      ...state,
      isSuccessSendPhoneNumber: false,
      isFailedSendPhoneNumber: false,
      isSuccessVerifyCode: false,
      isFailedVerifyCode: false,
    }),
    [actionTypes.VERIFY_PHONE_CALL]: state => ({
      ...state,
      isSuccessSendPhoneNumber: false,
      isFailedSendPhoneNumber: false,
      isSuccessVerifyCode: false,
      isFailedVerifyCode: false,
    }),
    [actionTypes.SUCCESS_VERIFY_PHONE]: state => ({
      ...state,
      isSuccessSendPhoneNumber: true,
      isFailedSendPhoneNumber: false,
      sendPhoneNumberError: '',
    }),
    [actionTypes.FAILED_VERIFY_PHONE]: (state, action) => ({
      ...state,
      isSuccessSendPhoneNumber: false,
      isFailedSendPhoneNumber: true,
      sendPhoneNumberError: action.payload.error,
    }),

    [actionTypes.CONFIRM_VERIFY_CODE]: state => ({
      ...state,
      isSuccessVerifyCode: false,
      isFailedVerifyCode: false,
    }),
    [actionTypes.SUCCESS_VERIFY_CODE]: state => ({
      ...state,
      isSuccessVerifyCode: true,
      isFailedVerifyCode: false,
      verifyCodeError: '',
    }),
    [actionTypes.FAILED_VERIFY_CODE]: (state, action) => ({
      ...state,
      isSuccessVerifyCode: false,
      isFailedVerifyCode: true,
      verifyCodeError: action.payload.error,
    }),
    [actionTypes.SET_PAYMENTS]: (state, action) => ({
      ...state,
      payments: action.payload,
    }),
    [actionTypes.ADD_PAYMENT]: (state, action) => ({
      ...state,
      payments: [].concat(state.payments, action.payload),
    }),
  },
  initialState,
)

/**
 ************************************************************
 * API連携 非同期アクション
 ************************************************************
 **/

// ユーザーを取得
function* fetchUser({ payload }) {
  const userId = payload.userId

  if (userId) {
    try {
      const results = yield call(RestClient.get, `1/users/${userId}`)
      yield put({ type: actionTypes.SET_USER, payload: results })
      if (typeof payload.fetched === 'function') {
        payload.fetched(results)
      }
    } catch (e) {
      yield put({ type: actionTypes.FETCH_FAILED_USER })
    }
  }
}

// ユーザーを更新
function* updateUser({ payload }: { onSuccess?: Function }) {
  const state = yield select()
  const { contact } = state.ui
  try {
    const results = yield call(RestClient.patchJson, '1/users/me', {
      contact: {
        family_name: contact.familyName,
        given_name: contact.givenName,
        postal_code: contact.postalCode,
        state: contact.state,
        city: contact.city,
        address_1: contact.address1,
      },
    })
    yield put({ type: actionTypes.SUCCESS_UPDATE_USER })
    yield put({ type: actionTypes.SET_USER, payload: results })

    if (typeof payload.onSuccess === 'function') {
      payload.onSuccess()
    }
  } catch (e) {
    yield put({ type: actionTypes.FAILED_UPDATE_USER })
  }
}

// SMS認証メッセージを送信
function* verifyToSendSms({ payload: { userId, phoneNumber } }) {
  try {
    yield call(RestClient.postJson, `1/users/${userId}/phone/send_sms`, {
      confirming_number: phoneNumber,
    })
    yield put({
      type: SET_UI_STATE,
      payload: {
        sentPhoneNumber: phoneNumber,
        sentSms: true,
      },
    })
    yield put({ type: actionTypes.SUCCESS_VERIFY_PHONE })
  } catch (e) {
    yield put({
      type: actionTypes.FAILED_VERIFY_PHONE,
      payload: {
        error: e.message,
      },
    })
  }
}

// 電話番号認証の電話を掛ける
function* verifyToMakeCall({ payload: { userId, phoneNumber } }) {
  try {
    yield call(RestClient.postJson, `1/users/${userId}/phone/make_call`, {
      confirming_number: phoneNumber,
    })
    yield put({
      type: SET_UI_STATE,
      payload: {
        sentPhoneNumber: phoneNumber,
        sentSms: false,
      },
    })
    yield put({ type: actionTypes.SUCCESS_VERIFY_PHONE })
  } catch (e) {
    yield put({
      type: actionTypes.FAILED_VERIFY_PHONE,
      payload: {
        error: e.message,
      },
    })
  }
}

// 認証コードの送信
function* confirmVerifyCode({ payload: { userId, code } }) {
  try {
    yield call(RestClient.patchJson, `1/users/${userId}/phone/confirm`, {
      confirmation_code: code,
    })
    yield put({
      type: SET_UI_STATE,
      payload: {
        hasConfirmedPhone: true,
      },
    })
    yield put({ type: actionTypes.SUCCESS_VERIFY_CODE })
  } catch (e) {
    yield put({
      type: actionTypes.FAILED_VERIFY_CODE,
      payload: {
        error: e.message,
      },
    })
  }
}

function* fetchPayments({ payload }) {
  const userId = payload.userId

  if (userId) {
    try {
      yield put({ type: SET_UI_STATE, payload: { isLoading: true } })
      const results = yield call(RestClient.get, `1/users/${userId}/payments`)
      yield put({ type: actionTypes.SET_PAYMENTS, payload: results })
    } catch (e) {
    } finally {
      yield put({ type: SET_UI_STATE, payload: { isLoading: false } })
    }
  }
}

function* removePoint({ payload }) {
  yield put({
    type: SET_UI_STATE,
    payload: {
      usePoint: null,
      usePointErrors: null,
    },
  })
  payload.requestParam.apply_point_amount = null
  yield put(actions.recalcGuestAmount(payload))
}

function* removeCoupon({ payload }) {
  yield put({
    type: SET_UI_STATE,
    payload: {
      couponCode: null,
      applyCouponCode: null,
      couponErrors: null,
    },
  })
  payload.requestParam.coupon_code = null
  yield put(actions.recalcGuestAmount(payload))
}

function* recalcGuestAmount({ payload }) {
  const { userId, reservationId, requestParam } = payload

  try {
    yield put({ type: SET_UI_STATE, payload: { isLoading: true } })
    const results = yield call(
      RestClient.patchJson,
      `/1/users/${userId}/reservations/${reservationId}/recalc_guest_amount`,
      requestParam,
    )
    yield put({ type: SET_UI_STATE, payload: { recalcReservation: results } })
  } catch (e) {
    const usePointErrors = (e.errors || [])
      .filter(e => e.field === 'apply_point_amount')
      .map(e => e.message)
    const couponErrors = (e.errors || [])
      .filter(e => e.field === 'coupon_code')
      .map(e => e.message)
    if (usePointErrors.length > 0) {
      yield put({
        type: SET_UI_STATE,
        payload: {
          usePointErrors,
        },
      })
    }
    if (couponErrors.length > 0) {
      yield put({
        type: SET_UI_STATE,
        payload: {
          couponErrors,
        },
      })
    }
  } finally {
    yield put({ type: SET_UI_STATE, payload: { isLoading: false } })
  }
}

function* payReservation({ payload }) {
  const { userId, reservationId, requestParam, onSuccess } = payload

  try {
    yield put({ type: SET_UI_STATE, payload: { isLoading: true } })
    const results = yield call(
      RestClient.patchJson,
      `/1/users/${userId}/reservations/${reservationId}/pay`,
      requestParam,
    )
    onSuccess()
  } catch (e) {
    showErrorMessage(e)
  } finally {
    yield put({ type: SET_UI_STATE, payload: { isLoading: false } })
  }
}

function* postPayment({ payload }) {
  const { userId, paymentToken } = payload

  try {
    yield put({ type: SET_UI_STATE, payload: { isLoading: true } })
    const results = yield call(
      RestClient.postJson,
      `/1/users/${userId}/payment`,
      {
        token: paymentToken,
      },
    )
    yield put({
      type: actionTypes.ADD_PAYMENT,
      payload: results,
    })
    yield put({
      type: SET_UI_STATE,
      payload: {
        cardNumber: [],
        cardHolderName: '',
        cardCvc: '',
        cardExpireMonth: null,
        cardExpireYear: null,
      },
    })
  } catch (e) {
    showErrorMessage(e)
  } finally {
    yield put({ type: SET_UI_STATE, payload: { isLoading: false } })
  }
}

function* createPaidContact({ payload }) {
  const { userId, param } = payload

  const requestParam = {
    email: param.paidMailAddress,
    contact_person_family_name: param.lastName,
    contact_person_given_name: param.firstName,
    contact_person_family_name_kana: param.lastNameKana,
    contact_person_given_name_kana: param.firstNameKana,
    corp_name: param.companyName,
    corp_name_kana: param.companyNameKana,
    ceo_family_name: param.presidentLastName,
    ceo_given_name: param.presidentFirstName,
    ceo_family_name_kana: param.presidentLastNameKana,
    ceo_given_name_kana: param.presidentFirstNameKana,
    postal_code: param.zipcode,
    state: param.state,
    city: param.prefectures,
    address_1: param.street,
    address_2: param.building,
    tel: param.phoneNumber,
    is_immediate: param.is_immediate,
    dryrun: param.dryrun,
  }

  try {
    yield put({ type: SET_UI_STATE, payload: { isLoading: true } })
    const results = yield call(
      RestClient.postJson,
      `/1/users/${userId}/paid_contact`,
      requestParam,
    )
    if (param.dryrun) {
      yield put({
        type: SET_UI_STATE,
        payload: { isConfirmPaidExamination: true },
      })
    } else {
      // paid_account_statusを確認するためにuserを取り直す
      const fetchedUser = yield call(RestClient.get, `1/users/${userId}`)
      yield put({ type: actionTypes.SET_USER, payload: fetchedUser })
    }
  } catch (e) {
    showErrorMessage(e)
    yield put({
      type: SET_UI_STATE,
      payload: {
        paidExamError: e.errors,
      },
    })
  } finally {
    yield put({ type: SET_UI_STATE, payload: { isLoading: false } })
  }
}

function* changePayment({ payload }) {
  const { userId, reservationId, requestParam, onSuccess } = payload

  try {
    yield put({ type: SET_UI_STATE, payload: { isLoading: true } })
    const results = yield call(
      RestClient.patchJson,
      `/1/users/${userId}/reservations/${reservationId}/change_payment`,
      requestParam,
    )
    onSuccess()
  } catch (e) {
    showErrorMessage(e)
  } finally {
    yield put({ type: SET_UI_STATE, payload: { isLoading: false } })
  }
}

/**
 ************************************************************
 * saga
 ************************************************************
 **/

export const userSagas = [
  takeEvery(actionTypes.FETCH_USER, fetchUser),
  takeEvery(actionTypes.UPDATE_USER, updateUser),
  takeEvery(actionTypes.VERIFY_PHONE_SMS, verifyToSendSms),
  takeEvery(actionTypes.VERIFY_PHONE_CALL, verifyToMakeCall),
  takeEvery(actionTypes.CONFIRM_VERIFY_CODE, confirmVerifyCode),
  takeEvery(actionTypes.FETCH_PAYMENTS, fetchPayments),
  takeEvery(actionTypes.RECALC_GUEST_AMOUNT, recalcGuestAmount),
  takeEvery(actionTypes.PAY_RESERVATION, payReservation),
  takeEvery(actionTypes.POST_PAYMENT, postPayment),
  takeEvery(actionTypes.REMOVE_POINT, removePoint),
  takeEvery(actionTypes.REMOVE_COUPON, removeCoupon),
  takeEvery(actionTypes.CREATE_PAID_CONTACT, createPaidContact),
  takeEvery(actionTypes.CHANGE_PAYMENT, changePayment),
]



// WEBPACK FOOTER //
// ./src/redux/modules/remote/user.js